import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Eye, Bot, BookOpen } from 'lucide-react';
import { createEditor } from 'lexical';
import { $generateHtmlFromNodes } from '@lexical/html';
import PlaygroundNodes from '../editor/nodes/PlaygroundNodes';
import { processMathEquation } from './utils/latexParser';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { DIFFICULTY_COLORS } from '../pages/LearningStatus/utils/constants';
import { fetchMathConcepts } from '../utils/mathConceptsUtils';
// AI 실행(LLM 비용)을 쓸 수 있는 계정 유형 — 판정은 이 상수 한 곳에서만 온다
import { AI_RUN_ROLES } from '../utils/roles';
import katex from 'katex';
import '../editor/themes/PlaygroundEditorTheme.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

// 개념 타입별 색상 (ProblemPreview 모달과 동일)
const getConceptTypeColor = (type) => {
  const colors = {
    DEF: 'rgba(239, 68, 68, 0.5)', AXM: 'rgba(249, 115, 22, 0.5)', IDN: 'rgba(245, 158, 11, 0.5)',
    THM: 'rgba(34, 197, 94, 0.5)', MTH: 'rgba(6, 182, 212, 0.5)', PRP: 'rgba(59, 130, 246, 0.5)',
    FOR: 'rgba(139, 92, 246, 0.5)', NTN: 'rgba(236, 72, 153, 0.5)',
  };
  return colors[type] || 'rgba(251, 146, 60, 0.5)';
};

// 개념명 내 $...$ LaTeX 렌더 (ProblemPreview 모달과 동일)
const renderLatex = (text) => {
  if (!text) return '';
  try {
    return String(text).replace(/\$([^$]+)\$/g, (match, formula) => {
      try { return katex.renderToString(formula, { throwOnError: false, displayMode: false }); }
      catch (e) { return match; }
    });
  } catch (e) { return text; }
};

const ProblemPreviewCards = ({ 
  problems = [], 
  selectedTest,
  onViewProblem,
  highlightedIndex,
  onHighlight,
  getProblemResult,
  isDesktop = false,
  showConcepts = false
}) => {
  const [loadedProblems, setLoadedProblems] = useState({});
  const [loadingProblems, setLoadingProblems] = useState({});
  const [visibleCards, setVisibleCards] = useState(new Set());
  const [conceptsByProblem, setConceptsByProblem] = useState({}); // pid -> [{order, concept_type, concept_name}]
  const scrollContainerRef = useRef(null);
  const cardRefs = useRef({});
  const observerRef = useRef(null);
  const { getAccessToken, userType } = useAuth();
  // AI 생성 버튼은 v2 [변형] 탭으로 간다 — 변형은 LLM 비용이 나고, 강사에게는 그 탭이 없다(ProblemHub 의 visibleSideTabs).
  // 그래서 강사에게는 버튼을 그리지 않는다(v2 카드 ProblemCardsV2 의 [AI 변형]과 같은 판정).
  const canRunAi = AI_RUN_ROLES.includes(userType);
  const navigate = useNavigate();

  // 문제 데이터 파싱
  const parsedProblems = problems.map((problem, index) => {
    let problemId, isCorrect, difficulty, address;
    
    // problems_list 구조인 경우
    if (problem.problem_id) {
      problemId = problem.problem_id;
      isCorrect = getProblemResult ? getProblemResult(selectedTest, problemId) : null;
      difficulty = problem.step;
      address = problem.address;
    }
    // sequence 배열 구조인 경우
    else if (Array.isArray(problem)) {
      problemId = problem[0];
      isCorrect = problem[6];
      difficulty = problem[1];
      const problemFromList = selectedTest?.problems_list?.find(p => p.problem_id === problemId);
      address = problemFromList?.address;
    }
    
    return {
      index,
      problemId,
      isCorrect,
      difficulty,
      address
    };
  }).filter(p => p.problemId);

  // showConcepts: 각 문제의 수학개념(order/타입/이름)을 카드에 바로 표시하기 위해 로드
  // by-prob-ids(문제→concept_ids) + 개념 카탈로그(fetchMathConcepts, 캐시)로 상세를 해석한다.
  const conceptProblemIdsKey = parsedProblems.map(p => p.problemId).join(',');
  useEffect(() => {
    if (!showConcepts) return;
    const ids = conceptProblemIdsKey ? conceptProblemIdsKey.split(',').map(Number).filter(Boolean) : [];
    if (ids.length === 0) { setConceptsByProblem({}); return; }
    let cancelled = false;
    (async () => {
      try {
        const catalog = await fetchMathConcepts();
        const byId = new Map();
        (catalog || []).forEach(mc => byId.set(String(mc.id), mc));
        const token = await getAccessToken();
        const BATCH = 100;
        const batches = [];
        for (let i = 0; i < ids.length; i += BATCH) batches.push(ids.slice(i, i + BATCH));
        const results = await Promise.all(batches.map(async (b) => {
          const r = await fetch(
            `${API_BASE_URL}/api/mathcepts/temp/by-prob-ids?problem_ids=${b.join(',')}`,
            { headers: { Authorization: token, 'Content-Type': 'application/json' }, credentials: 'include' }
          );
          if (!r.ok) return [];
          const j = await r.json();
          return j.data || [];
        }));
        if (cancelled) return;
        const map = {};
        results.flat().forEach((item) => {
          const details = (item.concept_ids || [])
            .map((cid) => {
              const mc = byId.get(String(cid));
              return mc ? { order: mc.order, concept_type: mc.concept_type, concept_name: mc.concept_name || mc.text } : null;
            })
            .filter(Boolean);
          map[String(item.problem_id)] = details;
        });
        setConceptsByProblem(map);
      } catch (e) {
        console.error('카드 수학개념 로드 실패:', e);
      }
    })();
    return () => { cancelled = true; };
  }, [showConcepts, conceptProblemIdsKey, getAccessToken]);

  // Lexical 데이터를 HTML로 변환
  const convertLexicalToHtml = useCallback((lexicalData) => {
    const editor = createEditor({
      nodes: [...PlaygroundNodes]
    });

    const processNode = (node) => {
      // node가 유효하지 않으면 빈 객체 반환
      if (!node || typeof node !== 'object') {
        console.warn('processNode: 유효하지 않은 노드:', node);
        return {};
      }
      
      if (node.type === 'equation') {
        // equation 필드가 있고 유효한 경우에만 처리
        if (node.equation !== undefined && node.equation !== null) {
          try {
            node.equation = processMathEquation(node.equation);
          } catch (error) {
            console.error('processMathEquation 오류:', error, '원본 equation:', node.equation);
            node.equation = String(node.equation || ''); // 안전한 문자열로 변환
          }
        }
      } else if (node.type === 'box-content') {
        node.className = 'PlaygroundEditorTheme__boxContent';
      }
      
      if (node.children && Array.isArray(node.children)) {
        node.children = node.children.map(child => processNode(child));
      }
      return node;
    };

    lexicalData.root = processNode(lexicalData.root);
    const editorState = editor.parseEditorState(lexicalData);

    let content = '';
    editor.setEditorState(editorState);
    editor.update(() => {
      content = $generateHtmlFromNodes(editor);
    });

    return content;
  }, []);

  // 배치 처리를 위한 상태 관리
  const BATCH_SIZE = 10; // 한 번에 로드할 문제 수
  const loadedBatchesRef = useRef(new Set()); // 로드된 배치 번호 추적
  const loadingBatchesRef = useRef(new Set()); // 로딩 중인 배치 번호 추적
  const parsedProblemsRef = useRef([]); // 최신 parsedProblems 참조
  const processedProblemsRef = useRef(new Set()); // 이미 처리된 문제 ID 추적 (무한 호출 방지)
  
  // 전역 배치 로딩 제한 (한 번에 하나의 배치만)
  const isAnyBatchLoadingRef = useRef(false);
  const batchLoadTimeouts = useRef({}); // 배치별 디바운싱
  
  // parsedProblems ref 업데이트
  useEffect(() => {
    parsedProblemsRef.current = parsedProblems;
  }, [parsedProblems]);

  // 배치 단위로 문제 로드 (무한 호출 완전 방지)
  const loadBatch = useCallback(async (batchIndex) => {
    console.log(`배치 ${batchIndex} 로드 시작 - 상태 체크:`, {
      이미로드됨: loadedBatchesRef.current.has(batchIndex),
      로딩중: loadingBatchesRef.current.has(batchIndex)
    });

    // 중복 로드 방지 - 더 엄격한 체크
    if (loadedBatchesRef.current.has(batchIndex) || loadingBatchesRef.current.has(batchIndex)) {
      console.log(`배치 ${batchIndex} 이미 처리됨, 건너뛰기`);
      return;
    }

    // 전역 로딩 제한 (한 번에 하나의 배치만)
    if (isAnyBatchLoadingRef.current) {
      console.log(`다른 배치 로딩 중, 배치 ${batchIndex} 대기`);
      // 100ms 후 재시도
      setTimeout(() => loadBatch(batchIndex), 100);
      return;
    }

    // 해당 배치의 문제 ID들 계산 (ref를 통한 안전한 접근)
    const currentProblems = parsedProblemsRef.current;
    const startIndex = batchIndex * BATCH_SIZE;
    const endIndex = Math.min(startIndex + BATCH_SIZE, currentProblems.length);
    const batchProblemIds = currentProblems.slice(startIndex, endIndex).map(p => p.problemId);
    
    if (batchProblemIds.length === 0) {
      console.log(`배치 ${batchIndex} 문제 없음`);
      return;
    }

    console.log(`배치 ${batchIndex} 실제 로드 시작:`, batchProblemIds);

    // 즉시 로딩 상태로 설정 (다른 호출 차단)
    loadingBatchesRef.current.add(batchIndex);
    isAnyBatchLoadingRef.current = true; // 전역 로딩 상태 설정
    
    // 배치 내 모든 문제를 즉시 "처리됨"으로 마킹 (무한 호출 방지)
    batchProblemIds.forEach(id => {
      const pid = String(id);
      processedProblemsRef.current.add(pid);
      setLoadingProblems(prev => ({ ...prev, [pid]: true }));
    });

    try {
      const token = await getAccessToken();
      if (!token) {
        throw new Error('인증 토큰이 없습니다.');
      }

      console.log(`배치 ${batchIndex} 로드 시작 (문제 ${startIndex + 1}-${endIndex}):`, batchProblemIds);

      const problemIdsString = batchProblemIds.join(',');
      const response = await fetch(
        `${API_BASE_URL}/api/problems/print/page?problem_ids=${problemIdsString}&page=1&per_page=50`,
        {
          headers: {
            'Authorization': token,
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        }
      );

      if (!response.ok) {
        throw new Error('배치 API 요청 실패');
      }

      const data = await response.json();
      
      if (data.status === 'success' && data.data) {
        const newLoadedProblems = {};
        
        data.data.forEach(problemData => {
          const pid = String(problemData.problem_id);
          
          if (problemData?.lexical_data?.lexical_data) {
            try {
              const htmlContent = convertLexicalToHtml(problemData.lexical_data.lexical_data);
              newLoadedProblems[pid] = htmlContent;
            } catch (error) {
              console.error(`문제 ${pid} lexical 변환 오류:`, error);
              newLoadedProblems[pid] = '<p class="text-red-500">문제를 표시하는데 실패했습니다.</p>';
            }
          } else {
            console.warn(`문제 ${pid}의 lexical_data가 없음:`, problemData);
            newLoadedProblems[pid] = '<p class="text-red-500">문제 내용을 불러올 수 없습니다.</p>';
          }
        });

        // 응답에 없는 문제들은 오류 처리
        batchProblemIds.forEach(id => {
          const pid = String(id);
          if (!newLoadedProblems[pid]) {
            newLoadedProblems[pid] = '<p class="text-red-500">문제를 불러오는데 실패했습니다.</p>';
          }
        });

        setLoadedProblems(prev => ({
          ...prev,
          ...newLoadedProblems
        }));

        loadedBatchesRef.current.add(batchIndex);
        console.log(`배치 ${batchIndex} 로드 성공 완료`);
      } else {
        console.warn(`배치 ${batchIndex} API 응답 실패:`, data);
        // API 응답 실패시 오류 처리
        const errorProblems = {};
        batchProblemIds.forEach(id => {
          const pid = String(id);
          errorProblems[pid] = '<p class="text-red-500">문제를 불러오는데 실패했습니다.</p>';
        });
        
        setLoadedProblems(prev => ({
          ...prev,
          ...errorProblems
        }));
      }
    } catch (error) {
      console.error(`배치 ${batchIndex} API 호출 오류:`, error);
      
      // 오류 발생시 오류 메시지 설정
      const errorProblems = {};
      batchProblemIds.forEach(id => {
        const pid = String(id);
        errorProblems[pid] = '<p class="text-red-500">문제를 불러오는데 실패했습니다.</p>';
      });
      
      setLoadedProblems(prev => ({
        ...prev,
        ...errorProblems
      }));
    } finally {
      // 로딩 상태 해제
      batchProblemIds.forEach(id => {
        const pid = String(id);
        setLoadingProblems(prev => ({ ...prev, [pid]: false }));
      });
      
      loadingBatchesRef.current.delete(batchIndex);
      isAnyBatchLoadingRef.current = false; // 전역 로딩 상태 해제
      console.log(`배치 ${batchIndex} 로딩 상태 해제 완료`);
    }
  }, [getAccessToken, convertLexicalToHtml]); // 안정적인 의존성만 유지



  // IntersectionObserver 설정
  useEffect(() => {
    // DOM이 준비될 때까지 대기
    const timer = setTimeout(() => {
      // Observer 생성
      observerRef.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const problemId = entry.target.getAttribute('data-problem-id');
            if (problemId && entry.isIntersecting) {
              const pid = String(problemId);
              // 가시 영역에 들어온 카드 추적
              setVisibleCards(prev => new Set([...prev, pid]));
              
              // 이미 처리된 문제는 건너뛰기 (무한 호출 방지)
              if (processedProblemsRef.current.has(pid)) {
                return;
              }
              
              // 해당 문제가 속한 배치 인덱스 계산
              const currentProblems = parsedProblemsRef.current;
              const problemIndex = currentProblems.findIndex(p => p.problemId === parseInt(problemId));
              if (problemIndex !== -1) {
                const batchIndex = Math.floor(problemIndex / BATCH_SIZE);
                
                // 중복 로드 방지 - 더 엄격한 체크
                if (!loadedBatchesRef.current.has(batchIndex) && !loadingBatchesRef.current.has(batchIndex)) {
                  console.log(`스크롤로 인한 배치 ${batchIndex} 로드 요청 (문제 ${problemId})`);
                  loadBatch(batchIndex);
                }
              }
            }
          });
        },
        {
          root: scrollContainerRef.current,
          rootMargin: '200px', // 200px 미리 로드
          threshold: 0.01
        }
      );

      // 이미 렌더링된 카드들에 observer 적용
      const cards = document.querySelectorAll('[data-problem-id]');
      cards.forEach(card => {
        if (observerRef.current) {
          observerRef.current.observe(card);
        }
      });
    }, 100);

    return () => {
      clearTimeout(timer);
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []); // 의존성 제거 - IntersectionObserver 콜백에서 직접 처리

  // 초기 로드: 첫 번째 배치(1-10번째) 즉시 로드 (한 번만)
  const initialLoadExecuted = useRef(false);
  const currentProblemsKey = useRef('');
  
  useEffect(() => {
    const problemsKey = parsedProblems.map(p => p.problemId).join(',');
    
    // 문제 목록이 변경된 경우에만 초기화 및 로드
    if (currentProblemsKey.current !== problemsKey) {
      console.log('문제 목록 변경 감지, 초기화 시작');
      
      // 상태 초기화
      currentProblemsKey.current = problemsKey;
      initialLoadExecuted.current = false;
      loadedBatchesRef.current.clear();
      loadingBatchesRef.current.clear();
      processedProblemsRef.current.clear(); // 처리된 문제 목록도 초기화
      isAnyBatchLoadingRef.current = false; // 전역 로딩 상태도 초기화
      
      // 첫 번째 배치 로드 (ref 기반으로 안전하게)
      if (parsedProblemsRef.current.length > 0) {
        console.log('초기 배치 로드 시작');
        initialLoadExecuted.current = true;
        
        // ref 기반 loadBatch 즉시 호출
        loadBatch(0);
      }
    }
  }, [parsedProblems.map(p => p.problemId).join(',')]); // 문제 ID가 변경될 때만 실행

  // 하이라이트된 인덱스가 변경되면 해당 카드로 스크롤 (비활성화됨)
  // useEffect(() => {
  //   if (highlightedIndex !== null && highlightedIndex !== undefined && cardRefs.current[highlightedIndex]) {
  //     const card = cardRefs.current[highlightedIndex];
  //     card.scrollIntoView({ behavior: 'smooth', block: 'center' });
  //   }
  // }, [highlightedIndex]);

  const handleCardClick = (index, problemData) => {
    if (onHighlight) {
      onHighlight(index, problemData);
    }
  };

  const getDifficultyColor = (difficulty) => {
    // DIFFICULTY_COLORS 상수 사용 (rgba 값을 그대로 사용)
    return DIFFICULTY_COLORS[difficulty - 1] || 'rgba(156, 163, 175, 0.7)';
  };

  const handleCreateNewProblem = (problemId) => {
    // 문제 v2 의 [변형] 탭으로 간다 — v1 문제생성(/problem-agent) 메뉴를 가렸고(2026-10-21),
    // v2 에서 같은 기능(원본 하나로 새 문제 만들기)의 이름이 「변형」이다.
    // 탭은 주소(?tab=)가 정본이라 route state 를 넘기지 않는다(ProblemHub.js 의 탭 주석).
    navigate(`/problem-hub/problem/${problemId}?tab=variation`);
  };

  if (parsedProblems.length === 0) {
    return (
      <div className="flex items-center justify-center py-8">
        <p className="text-xs text-gray-500">문제 정보가 없습니다.</p>
      </div>
    );
  }

  return (
    <div className={`relative ${isDesktop ? 'h-full -mx-4' : '-mx-4'}`}>
      {/* 세로 스크롤 컨테이너 */}
      <div 
        ref={scrollContainerRef}
        className={`problem-preview-cards-container ${isDesktop ? 'overflow-y-auto h-full' : 'w-full'}`}
        style={{
          maxHeight: isDesktop ? '100%' : 'none',
          height: isDesktop ? '100%' : 'auto',
          overflowX: 'visible',
          overflowY: isDesktop ? undefined : 'visible',
          backgroundColor: 'transparent'
        }}
      >
        <div className="px-4 py-[8px] bg-white min-h-full">
          {parsedProblems.map((problem, idx) => (
            <div
              key={problem.problemId}
              data-problem-id={problem.problemId}
              ref={el => {
                cardRefs.current[idx] = el;
                // Observer에 등록
                if (el && observerRef.current) {
                  observerRef.current.observe(el);
                }
              }}
              onClick={() => handleCardClick(idx, problem)}
              className={`relative cursor-pointer rounded-3xl border bg-white p-[14.4px] shadow-lg transition-all duration-300 hover:shadow-2xl hover:scale-[1.02] hover:border-primary/20 ${
                idx === highlightedIndex 
                  ? 'border-transparent ring-2 ring-primary' 
                  : 'border-gray-200'
              }`}
              style={{ 
                width: '100%', 
                marginBottom: idx === parsedProblems.length - 1 ? '0' : '14.4px'
              }}
            >
              {/* 카드 헤더 */}
              <div className="mb-2 flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div 
                    className="problem-number text-2xl font-bold"
                    style={{ 
                      color: problem.isCorrect === null || problem.isCorrect === undefined ? '#000000' :
                             problem.isCorrect ? '#4ABC85' : '#F9344B',
                      minWidth: '36px'
                    }}
                  >
                    {String(problem.index + 1).padStart(2, '0')}
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">PID: {problem.problemId}</span>
                    {/* 난이도 표시 */}
                    <div 
                      className="rounded-full px-2 py-0.5 text-[10px] font-bold text-white"
                      style={{ backgroundColor: getDifficultyColor(problem.difficulty) }}
                    >
                      Lv.{problem.difficulty || '-'}
                    </div>
                  </div>
                </div>
                
                <div className="flex gap-1">
                  {/* AI 생성 버튼 */}
                  {canRunAi && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCreateNewProblem(problem.problemId);
                      }}
                      className="flex items-center gap-0.5 rounded-full bg-[#4ABC85] px-2.5 py-0.5 text-[10px] text-white transition-colors hover:bg-[#42a478]"
                      title="새 문제 AI 생성"
                    >
                      <Bot size={16} />
                      {/* <span>AI생성</span> */}
                    </button>
                  )}
                  {/* 상세 보기 버튼 */}
                  {onViewProblem && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onViewProblem([problem.problemId], problem.index, problem.isCorrect);
                      }}
                      className="flex items-center gap-0.5 rounded-full bg-blue-500 px-2.5 py-0.5 text-[10px] text-white transition-colors hover:bg-blue-600"
                      title="문제 상세 보기"
                    >
                      <Eye size={16} />
                      {/* <span>상세</span> */}
                    </button>
                  )}
                </div>
              </div>

              {/* 수학 개념 (showConcepts 일 때, 모달의 '수학 개념' 영역을 카드에 직접 표시) */}
              {showConcepts && conceptsByProblem[String(problem.problemId)]?.length > 0 && (
                <div className="mb-2 rounded-lg bg-gray-50 p-2" onClick={(e) => e.stopPropagation()}>
                  <div className="mb-1 flex items-center gap-2">
                    <BookOpen className="h-4 w-4 text-gray-600" />
                    <span className="text-xs font-medium text-gray-600">수학 개념</span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {conceptsByProblem[String(problem.problemId)].map((mc, i) => (
                      <div
                        key={i}
                        className="flex items-center gap-1 rounded-3xl border border-gray-200 px-2 py-0.5 text-xs text-gray-700"
                      >
                        {mc.order != null && (
                          <span
                            className="inline-flex items-center justify-center font-semibold text-gray-700"
                            style={{ backgroundColor: 'rgba(156, 163, 175, 0.3)', borderRadius: '6px', width: '24px', height: '16px', minWidth: '24px', fontSize: '9px' }}
                          >
                            {mc.order}
                          </span>
                        )}
                        {mc.concept_type && (
                          <span
                            className="inline-flex items-center justify-center font-semibold text-gray-800"
                            style={{ backgroundColor: getConceptTypeColor(mc.concept_type), borderRadius: '6px', width: '24px', height: '16px', minWidth: '24px', fontSize: '8px' }}
                          >
                            {mc.concept_type}
                          </span>
                        )}
                        <span dangerouslySetInnerHTML={{ __html: renderLatex(mc.concept_name) }} />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 문제 미리보기 내용 */}
              <div className="problem-preview-content relative rounded-2xl bg-gradient-to-br from-gray-50 to-gray-100/50 p-3 mt-2 overflow-visible">
                {loadingProblems[String(problem.problemId)] ? (
                  <div className="flex h-24 items-center justify-center">
                    <div className="size-6 animate-spin rounded-full border-b-2 border-gray-600"></div>
                  </div>
                ) : loadedProblems[String(problem.problemId)] ? (
                  <div 
                    className="problem-content-preview"
                    style={{
                      fontSize: '12px',
                      lineHeight: '1.5',
                      // 가시 영역 밖에서는 렌더링 최소화
                      visibility: visibleCards.has(String(problem.problemId)) ? 'visible' : 'hidden',
                      minHeight: visibleCards.has(String(problem.problemId)) ? 'auto' : '60px',
                      maxHeight: visibleCards.has(String(problem.problemId)) ? 'none' : '60px',
                      overflow: visibleCards.has(String(problem.problemId)) ? 'visible' : 'hidden'
                    }}
                    dangerouslySetInnerHTML={{ __html: loadedProblems[String(problem.problemId)] }}
                  />
                ) : (
                  <div className="flex h-24 items-center justify-center text-gray-400">
                    <p>클릭하여 문제 보기</p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>


      {/* 스타일 */}
      <style>{`
        .problem-content-preview *:not(.katex):not(.katex *) {
          font-family: 'Pretendard', sans-serif !important;
          font-weight: 400 !important;
          font-size: 12px !important;
          line-height: 1.5 !important;
        }
        .problem-content-preview .katex {
          font-size: 1em !important;
        }
        .problem-content-preview .PlaygroundEditorTheme__boxContent {
          border: 1px solid black;
          padding: 10px;
          margin: 10px auto;
          max-width: 100%;
          border-radius: 4px;
          background-color: #ffffff;
        }
        .problem-container {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
        }
        .problem-content-wrapper {
          display: flex;
          align-items: flex-start;
          width: 100%;
        }
        .problem-content-preview {
          overflow-wrap: break-word;
          word-break: break-word;
        }
        .problem-content-preview .PlaygroundEditorTheme__layoutContainer {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
          max-width: 100%;
        }
        .problem-content-preview [data-lexical-layout-container] {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          grid-template-rows: repeat(3, auto);
          max-width: 100%;
          gap: 8px;
        }
        .PlaygroundEditorTheme__imageContainer {
          text-align: center;
          margin: 16px 0;
        }
        .problem-content-preview img {
          max-width: 100%;
          height: auto;
          display: block;
          margin: 0 auto;
        }
        .problem-preview-cards-container::-webkit-scrollbar {
          width: 6px;
        }
        .problem-preview-cards-container::-webkit-scrollbar-track {
          background: transparent;
        }
        .problem-preview-cards-container::-webkit-scrollbar-thumb {
          background: rgba(156, 163, 175, 0.3);
          border-radius: 3px;
          backdrop-filter: blur(10px);
        }
        .problem-preview-cards-container::-webkit-scrollbar-thumb:hover {
          background: rgba(156, 163, 175, 0.5);
        }
        
        /* Firefox */
        .problem-preview-cards-container {
          scrollbar-width: thin;
          scrollbar-color: rgba(156, 163, 175, 0.3) transparent;
        }
      `}</style>
    </div>
  );
};

export default React.memo(ProblemPreviewCards);